/**
 * AppSidebar.tsx — Polished left navigation sidebar for GenWizard workspace.
 *
 * Replaces the existing `Sidebar` component.  Uses only `gw-app-sidebar-*`
 * CSS classes defined in theme.ts so no external CSS file is needed.
 *
 * Usage:
 *   import AppSidebar from '../common/AppSidebar';
 *
 *   <AppSidebar
 *     items={[
 *       { key: 'businessEntity', label: 'DQ Rules',        sublabel: 'Fields & Groups', icon: '🗄️' },
 *       { key: 'srcSystem',      label: 'Source Systems',  icon: '🔌', badge: '3' },
 *       { key: 'jobMonitoring',  label: 'Jobs',            icon: '👁' },
 *     ]}
 *     activeKey="businessEntity"
 *     onSelect={(key) => setState({ tabKey: key })}
 *     collapsed={false}
 *     onToggleCollapse={() => toggleSidebar()}
 *     appName="Material MDM"
 *   />
 *
 * The `icon` prop accepts any string — emoji works perfectly here because
 * it lives inside a fixed-size box, not inline with text.
 *
 * `badge` is an optional short string shown as a pill on the right (e.g. counts).
 *
 * FontAwesome icons: pass a rendered <FontAwesomeIcon /> as `iconNode` instead
 * of `icon` if you prefer vector icons.
 */

import React, { ReactNode } from 'react';
import { injectTheme, T } from '../theme/theme';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SidebarItem {
  key: string;
  label: string;
  sublabel?: string;
  /** Emoji or short string rendered in the icon box. */
  icon?: string;
  /** Alternatively, pass a rendered icon node (e.g. <FontAwesomeIcon />). */
  iconNode?: ReactNode;
  /** Short badge text shown on the right (e.g. counts, "NEW"). */
  badge?: string;
  /** Separator / group label rendered above this item. */
  groupLabel?: string;
  /** Disable interaction. */
  disabled?: boolean;
}

interface AppSidebarProps {
  items: SidebarItem[];
  activeKey: string;
  onSelect: (key: string) => void;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  /** Displayed in the sidebar header when expanded. */
  appName?: string;
  /** Optional footer content (e.g. version, user avatar). */
  footer?: ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

const AppSidebar: React.FC<AppSidebarProps> = ({
  items,
  activeKey,
  onSelect,
  collapsed = false,
  onToggleCollapse,
  appName,
  footer,
}) => {
  React.useEffect(() => {
    injectTheme();
  }, []);

  return (
    <aside
      className={`gw-app-sidebar ${collapsed ? 'collapsed' : 'expanded'}`}
      style={{ transition: 'width .2s ease' }}
    >
      {/* ── Header ── */}
      <div className="gw-app-sidebar-header">
        {!collapsed && (
          <span className="gw-app-sidebar-app-label" title={appName}>
            {appName || 'Navigation'}
          </span>
        )}
        {onToggleCollapse && (
          <button
            className="gw-app-sidebar-toggle"
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{ marginLeft: collapsed ? 'auto' : undefined }}
          >
            {collapsed ? '›' : '‹'}
          </button>
        )}
      </div>

      {/* ── Nav items ── */}
      <div className="gw-app-sidebar-scroll">
        {items.map((item) => (
          <React.Fragment key={item.key}>
            {/* Group label */}
            {item.groupLabel && !collapsed && (
              <div className="gw-app-sidebar-group-label">
                {item.groupLabel}
              </div>
            )}

            {/* Nav row */}
            <div
              className={`gw-app-sidebar-item${activeKey === item.key ? ' active' : ''}${item.disabled ? ' disabled' : ''}`}
              onClick={() => !item.disabled && onSelect(item.key)}
              title={collapsed ? item.label : undefined}
              style={{
                opacity: item.disabled ? 0.45 : 1,
                pointerEvents: item.disabled ? 'none' : 'auto',
                justifyContent: collapsed ? 'center' : undefined,
              }}
            >
              {/* Icon box */}
              <div className="gw-app-sidebar-item-icon">
                {item.iconNode ?? (
                  <span style={{ fontSize: 14, lineHeight: 1 }}>
                    {item.icon ?? '○'}
                  </span>
                )}
              </div>

              {/* Label + sublabel */}
              {!collapsed && (
                <div className="gw-app-sidebar-item-text">
                  <span className="gw-app-sidebar-item-label">{item.label}</span>
                  {item.sublabel && (
                    <span className="gw-app-sidebar-item-sublabel">{item.sublabel}</span>
                  )}
                </div>
              )}

              {/* Badge */}
              {!collapsed && item.badge && (
                <span className="gw-app-sidebar-item-badge">{item.badge}</span>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* ── Footer ── */}
      {footer && (
        <div className="gw-app-sidebar-footer">
          {collapsed ? null : footer}
        </div>
      )}
    </aside>
  );
};

export default AppSidebar;